import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Stack,
  IconButton,
  Backdrop,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Icons } from "../../../assets/icon";
import { enqueueSnackbar } from "notistack";
import { useCloverPurchaseCard } from "../../../../services/api/card.api";
import { useBookFlashCard } from "../Agora/apis/NewKarokeApi";

interface PaymentForFlashPeepingProps {
  open: boolean;
  onClose: () => void;
  streamId: string;
  flashCardId: number;
  title: string;
  peepPrice: number;
  purchaseChip?: number;
  earnedChip?: number;
  cardId?: string;
}

const PaymentForKaraoke: React.FC<PaymentForFlashPeepingProps> = ({
  open,
  onClose,
  flashCardId,
  streamId,
  title,
  peepPrice,
  earnedChip = 100, // Default balance
  purchaseChip = 100,
  cardId,
}) => {
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();

  const { mutate: purchaseCard } = useBookFlashCard({
    key: ["flash-card-peeping"],
    params: { flash_card_id: flashCardId },
  });

  const handlePeep = async () => {
    setLoading(true);
    try {
      await purchaseCard(
        { flash_card_id: flashCardId },
        {
          onSuccess: (data) => {
            enqueueSnackbar("Ticket purchased successfully", {
              variant: "success",
            });
            navigate(
              `/flash-live-stream-user/${streamId}?cardId=${flashCardId}`,
            );
            setShowConfirmation(false);
            onClose();
          },
          onError: (apiError: any) => {
            console.log("Purchase onError:", apiError);
            enqueueSnackbar(
              apiError?.response?.data?.message ||
                "Something went wrong in Flash Purchase",
              { variant: "error" },
            );
          },
        },
      );
    } catch (error) {
      console.error("Flash peep purchase failed:", error);
      enqueueSnackbar("Failed to purchase peep access", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmProceed = () => {
    handlePeep();
  };

  return (
    <>
      <Modal
        open={open}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300 } }}
      >
        <Fade in={open}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            sx={{
              p: 4,
              bgcolor: "background.paper",
              width: 380,
              mx: "auto",
              mt: "15vh",
              borderRadius: 3,
              boxShadow: 24,
              position: "relative",
            }}
          >
            {/* Close Icon */}
            <IconButton
              onClick={() => navigate(`/`)}
              sx={{ position: "absolute", top: 12, right: 12 }}
            >
              <CloseIcon />
            </IconButton>

            {/* Header */}
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <LiveTvIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Join Flash Stream
              </Typography>
            </Stack>

            {/* Content */}
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enjoy the flash stream of {title}
            </Typography>

            <Typography fontWeight={600} fontSize={16} mb={1}>
              {title}
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={1}>
              Coin:{" "}
              <strong style={{ color: "#6366F1" }}>
                <Icons.Coin /> {peepPrice}
              </strong>
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Purchase Chip:{" "}
              <strong style={{ color: "#6366F1" }}>
                <Icons.Coin /> {purchaseChip}
              </strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Earned Chip:{" "}
              <strong style={{ color: "#6366F1" }}>
                <Icons.Coin /> {earnedChip}
              </strong>
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Actions */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate(`/TNPage`)}
                disabled={loading}
                sx={{
                  fontFamily: "Lexend",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  borderColor: "#D1D5DB",
                  color: "#374151",
                  "&:hover": {
                    backgroundColor: "#F3F4F6",
                    borderColor: "#9CA3AF",
                  },
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleConfirmClick}
                disabled={loading || purchaseChip < peepPrice}
                sx={{
                  fontFamily: "Lexend",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  backgroundColor: "#4F46E5",
                  "&:hover": {
                    backgroundColor: "#4338CA",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Join Now"
                )}
              </Button>
            </Stack>
          </Box>
        </Fade>
      </Modal>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "Lexend", fontWeight: 600 }}>
          Confirm Purchase
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to spend{" "}
            <strong style={{ color: "#6366F1" }}>
              <Icons.Coin /> {peepPrice}
            </strong>{" "}
            to join this flash stream?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setShowConfirmation(false)}
            sx={{
              fontFamily: "Lexend",
              fontWeight: 600,
              textTransform: "none",
              color: "#374151",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmProceed}
            variant="contained"
            sx={{
              fontFamily: "Lexend",
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: "#4F46E5",
              "&:hover": {
                backgroundColor: "#4338CA",
              },
            }}
          >
            Confirm & Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentForKaraoke;
